import { TrendingUpIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import Box from '../../../uikit/Box';
import { CommonDateFormat } from '../../common/CommonDateFormat';
import { useMobxInstance } from '../../../utils/mobxUtil';
import { apiClient } from '../../../utils/apiClient';
import firebaseStore from '../../../stores/firebaseStore';
import { StrategyWeightResponse } from '../../../../server/response/responseScheme';

class ViewStore {
    weightHistory: StrategyWeightResponse[] = [];
    isLoading = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(strategyHash: string) {
        this.isLoading = true;

        try {
            // 로그인 확인
            const isLoggedIn = await firebaseStore.isLoggedIn();

            if (isLoggedIn) {
                try {
                    const weightHistory = await apiClient.strategy.member.getWeightHistory.$query({
                        hash: strategyHash,
                    });

                    runInAction(() => {
                        this.weightHistory = weightHistory;
                    });
                } catch (error) {
                    console.error('Failed to fetch weight history:', error);
                }
            }

            runInAction(() => {
                this.isLoading = false;
            });
        } catch (error) {
            console.error('Failed to fetch rebalance history:', error);
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    // Computed: 리밸런싱 히스토리
    get rebalanceHistory() {
        return this.weightHistory.map((item, index) => ({
            date: item.rebalanceDate,
            // TODO 리밸런싱 내용 추가 필요
            /*
             * 예:
             * {
             *   isInitial: true,
             *   stocks: [{ ticker: 'AAPL', weight: 0.5 }, { ticker: 'GOOG', weight: 0.5 }]
             *   changes: [{
             *     ticker: 'AAPL',
             *     weight: 0.5,
             *     change: 0.1,
             *     isNew: true,
             *     isRemoved: false,
             *   }, {
             *     ticker: 'GOOG',
             *     weight: 0.5,
             *     change: -0.1,
             *     isNew: false,
             *     isRemoved: false,
             *   }]
             * }
             */
            changes: '리밸런싱',
        }));
    }
}

interface Props {
    strategyHash: string;
}

const StrategyRebalanceHistoryBox = observer(({ strategyHash }: Props) => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch(strategyHash);
    }, [viewStore, strategyHash]);

    if (viewStore.isLoading) {
        return <Box isLoading={true} />;
    }

    // 히스토리가 없으면 렌더링하지 않음
    if (viewStore.rebalanceHistory.length === 0) {
        return null;
    }

    return (
        <Box>
            <Box.Title title="변경 기록" />
            <div className="flex flex-col gap-3">
                {viewStore.rebalanceHistory.map((item, index) => {
                    return (
                        <div
                            key={index}
                            className="border-base-300 flex items-start gap-3 border-l-2 pl-3"
                        >
                            <div className="bg-primary/10 text-primary mt-0.5 shrink-0 rounded-full p-1">
                                <TrendingUpIcon className="size-3" />
                            </div>
                            <div className="flex-1">
                                <div className="text-base-content/60 text-xs">
                                    <CommonDateFormat date={item.date} fromNow={true} />
                                </div>
                                <div className="mt-1 text-sm">{item.changes}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Box>
    );
});

export default StrategyRebalanceHistoryBox;
